import React, { useEffect, useRef } from 'react';

const BackgroundAnimation = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        let animationFrameId;
        let particles = [];

        const setCanvasSize = () => {
            const parent = canvas.parentElement;
            if (parent) {
                canvas.width = parent.offsetWidth;
                canvas.height = parent.offsetHeight;
            }
        };

        const handleResize = () => {
            setCanvasSize();
            initParticles();
        };

        window.addEventListener('resize', handleResize);
        setCanvasSize();

        const particleCount = typeof window !== 'undefined' && window.innerWidth < 768 ? 30 : 70;
        const colors = ['#ff014f', '#6366f1', '#a855f7', '#0ea5e9'];
        const CONNECTION_DIST = 120;
        const CELL_SIZE = CONNECTION_DIST;

        class Particle {
            constructor() {
                this.x = Math.random() * canvas.width;
                this.y = Math.random() * canvas.height;
                this.size = Math.random() * 1.5 + 0.5;
                this.speedX = (Math.random() - 0.5) * 0.3;
                this.speedY = (Math.random() - 0.5) * 0.3 - 0.15;
                this.color = colors[Math.floor(Math.random() * colors.length)];
                this.alpha = Math.random() * 0.5 + 0.1;
            }

            update() {
                this.x += this.speedX;
                this.y += this.speedY;
                if (this.x < 0) this.x = canvas.width;
                if (this.x > canvas.width) this.x = 0;
                if (this.y < 0) this.y = canvas.height;
                if (this.y > canvas.height) this.y = 0;
            }

            draw() {
                ctx.save();
                ctx.globalAlpha = this.alpha;
                ctx.beginPath();
                ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
                ctx.fillStyle = this.color;
                ctx.shadowBlur = 8;
                ctx.shadowColor = this.color;
                ctx.fill();
                ctx.restore();
            }
        }

        const initParticles = () => {
            particles = [];
            for (let i = 0; i < particleCount; i++) {
                particles.push(new Particle());
            }
        };

        // Spatial grid — O(n) instead of O(n²)
        const buildGrid = () => {
            const grid = {};
            for (let i = 0; i < particles.length; i++) {
                const cellX = Math.floor(particles[i].x / CELL_SIZE);
                const cellY = Math.floor(particles[i].y / CELL_SIZE);
                const key = `${cellX},${cellY}`;
                if (!grid[key]) grid[key] = [];
                grid[key].push(i);
            }
            return grid;
        };

        const drawConnections = () => {
            const grid = buildGrid();
            const neighborOffsets = [[0, 0], [1, 0], [-1, 1], [0, 1], [1, 1]];
            for (const key in grid) {
                const [cx, cy] = key.split(',').map(Number);
                const cellParticles = grid[key];
                for (const [dx, dy] of neighborOffsets) {
                    const neighborParticles = grid[`${cx + dx},${cy + dy}`];
                    if (!neighborParticles) continue;
                    for (const i of cellParticles) {
                        for (const j of neighborParticles) {
                            if (i >= j) continue;
                            const dxp = particles[i].x - particles[j].x;
                            const dyp = particles[i].y - particles[j].y;
                            const distSq = dxp * dxp + dyp * dyp;
                            if (distSq < CONNECTION_DIST * CONNECTION_DIST) {
                                const distance = Math.sqrt(distSq);
                                ctx.save();
                                ctx.globalAlpha = (1 - distance / CONNECTION_DIST) * 0.15;
                                ctx.beginPath();
                                ctx.moveTo(particles[i].x, particles[i].y);
                                ctx.lineTo(particles[j].x, particles[j].y);
                                ctx.strokeStyle = particles[i].color;
                                ctx.lineWidth = 0.5;
                                ctx.stroke();
                                ctx.restore();
                            }
                        }
                    }
                }
            }
        };

        const animate = () => {
            if (document.hidden) {
                animationFrameId = requestAnimationFrame(animate);
                return;
            }
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            particles.forEach((p) => { p.update(); p.draw(); });
            drawConnections();
            animationFrameId = requestAnimationFrame(animate);
        };

        initParticles();
        animate();

        return () => {
            window.removeEventListener('resize', handleResize);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 pointer-events-none z-0"
            style={{ opacity: 0.7 }}
        />
    );
};

export default BackgroundAnimation;
